'use client'

import { useEffect, useRef } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'

import { CityGridField } from './CityGridField'
import { DatePickerAirbnb } from '@/src/components/ui/DatePickerAirbnb'
import type { FlowFieldConfig, FlowFieldOption } from '@/src/lib/flow-engine/types'
import { useRemoteOptions } from '@/src/lib/flow-engine/useRemoteOptions'
import { verifyOldDraft } from '@/src/services/drafts.api'

interface DynamicFieldProps {
  field: FlowFieldConfig
  /** All current form values, used to evaluate visibleWhen & parent deps */
  allValues: Record<string, unknown>
  /** Called when verifyold detects an existing draft for the blurred email */
  onEmailDraftFound?: (_email: string) => void
  /** Called when a field's display label is resolved (e.g. user picks a select option) */
  onFieldLabelResolved?: (_labels: Record<string, string>) => void
}

/**
 * Evaluate whether a field should be visible based on its visibleWhen condition.
 */
function isFieldVisible(field: FlowFieldConfig, allValues: Record<string, unknown>): boolean {
  const evaluateCondition = ({
    field: depField,
    operator,
    value
  }: NonNullable<FlowFieldConfig['visibleWhen']>): boolean => {
    const current = allValues[depField]

    switch (operator) {
      case 'eq':
        return current === value
      case 'neq':
        return current !== value
      case 'in':
        return Array.isArray(value) && value.includes(String(current ?? ''))
      case 'notIn':
        return Array.isArray(value) && !value.includes(String(current ?? ''))
      default:
        return true
    }
  }

  const singleConditionOk = field.visibleWhen ? evaluateCondition(field.visibleWhen) : true
  const allConditionsOk = field.visibleWhenAll?.every(evaluateCondition) ?? true
  const anyGroupOk = field.visibleWhenAny
    ? field.visibleWhenAny.some((group) => group.every(evaluateCondition))
    : true

  return singleConditionOk && allConditionsOk && anyGroupOk
}

export { isFieldVisible }

export const DynamicField: React.FC<DynamicFieldProps> = ({
  field,
  allValues,
  onEmailDraftFound,
  onFieldLabelResolved
}) => {
  const {
    control,
    setValue,
    formState: { errors }
  } = useFormContext()
  const { t, i18n } = useTranslation()
  const lastVerifiedEmailRef = useRef<string>('')
  const verifyDebounceTimerRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (verifyDebounceTimerRef.current !== null) {
        window.clearTimeout(verifyDebounceTimerRef.current)
      }
    }
  }, [])

  const resolveFieldText = (defaultKey: string, configuredText?: string): string => {
    if (i18n.exists(defaultKey)) {
      return t(defaultKey)
    }

    if (configuredText && i18n.exists(configuredText)) {
      return t(configuredText)
    }

    return configuredText ?? defaultKey
  }

  // Translated labels, placeholders, helper texts
  const label = resolveFieldText(`fields.${field.key}.label`, field.label)
  const placeholder = field.placeholder
    ? resolveFieldText(`fields.${field.key}.placeholder`, field.placeholder)
    : undefined
  const helperText = field.helperText
    ? resolveFieldText(`fields.${field.key}.helperText`, field.helperText)
    : undefined
  const hasHtmlHelperText = Boolean(helperText && /<[^>]+>/.test(helperText))
  const checkboxText = field.checkboxText
    ? i18n.exists(field.checkboxText)
      ? t(field.checkboxText)
      : field.checkboxText
    : undefined

  // Resolve remote options if configured
  const parentValue = field.remoteOptions?.dependsOn
    ? (allValues[field.remoteOptions.dependsOn] as string | undefined)
    : undefined
  const { options: remoteOpts, loading: remoteLoading } = useRemoteOptions(
    field.remoteOptions,
    parentValue
  )

  // If field should be hidden, render nothing
  if (!isFieldVisible(field, allValues)) return null

  // Hidden/internal field: keep in form state but don't render UI
  if (field.type === 'hidden') return null

  // ── City Grid ─────────────────────────────────────────────
  if (field.type === 'city-grid') {
    return (
      <CityGridField
        field={field}
        options={remoteOpts}
        loading={remoteLoading}
        onFieldLabelResolved={onFieldLabelResolved}
      />
    )
  }

  // Merge static + remote options
  const options: FlowFieldOption[] = field.options ?? remoteOpts

  const resolveOptionLabel = (opt: FlowFieldOption): string => {
    const byValueKey = `options.${field.key}.${opt.value}`

    if (i18n.exists(byValueKey)) {
      return t(byValueKey)
    }

    if (typeof opt.label === 'string' && i18n.exists(opt.label)) {
      return t(opt.label)
    }

    return String(opt.label ?? opt.value ?? '')
  }

  const error = errors[field.key]
  const errorMessage = error && 'message' in error ? (error.message as string) : undefined

  // ── Checkbox ──────────────────────────────────────────────
  if (field.type === 'checkbox') {
    return (
      <Controller
        name={field.key}
        control={control}
        render={({ field: f }) => (
          <div>
            <FormControlLabel
              control={<Checkbox checked={!!f.value} onChange={f.onChange} />}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '1.4rem',
                  '& a': {
                    color: 'primary.main'
                  }
                }
              }}
              label={
                checkboxText ? <span dangerouslySetInnerHTML={{ __html: checkboxText }} /> : label
              }
            />
            {errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
          </div>
        )}
      />
    )
  }

  // ── Radio ─────────────────────────────────────────────────
  if (field.type === 'radio') {
    return (
      <Controller
        name={field.key}
        control={control}
        render={({ field: f }) => (
          <FormControl fullWidth error={!!errorMessage}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>
              {label}
              {field.required ? ' *' : ''}
            </InputLabel>
            <RadioGroup
              value={f.value ?? ''}
              onChange={(e) => {
                f.onChange(e)
                if (onFieldLabelResolved) {
                  const selectedOpt = options.find((opt) => opt.value === e.target.value)
                  if (selectedOpt) {
                    const resolvedLabel = resolveOptionLabel(selectedOpt)
                    onFieldLabelResolved({ [field.key]: resolvedLabel })
                  }
                }
              }}
              onBlur={f.onBlur}
              row={field.row ?? false}
            >
              {options.map((opt, index) => (
                <FormControlLabel
                  key={`${opt.value}-${index}`}
                  value={opt.value}
                  control={<Radio size="small" />}
                  label={resolveOptionLabel(opt)}
                  disabled={remoteLoading}
                />
              ))}
            </RadioGroup>
            {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
            {field.helperText && !errorMessage && (
              <FormHelperText>
                {hasHtmlHelperText ? (
                  <span dangerouslySetInnerHTML={{ __html: helperText ?? '' }} />
                ) : (
                  helperText
                )}
              </FormHelperText>
            )}
          </FormControl>
        )}
      />
    )
  }

  // ── Select ────────────────────────────────────────────────
  if (field.type === 'select') {
    return (
      <Controller
        name={field.key}
        control={control}
        render={({ field: f }) => {
          const shouldSortOptions = field.sortOptions ?? true
          const mainOptions = shouldSortOptions
            ? [...options].sort((a, b) => String(a.label).localeCompare(String(b.label)))
            : options
          const prependOpts = field.remoteOptions?.prependOptions ?? []
          const appendOpts = field.remoteOptions?.appendOptions ?? []
          const sortedOptions = [...prependOpts, ...mainOptions, ...appendOpts]

          // Only set value if it exists in available options, to avoid MUI warnings
          // when options are still loading or undefined
          const selectValue = sortedOptions.some((opt) => String(opt.value) === String(f.value))
            ? f.value
            : ''

          return (
            <FormControl fullWidth variant="standard" error={!!errorMessage}>
              <InputLabel shrink>
                {label}
                {field.required ? ' *' : ''}
              </InputLabel>
              <Select
                value={selectValue}
                onChange={(event) => {
                  f.onChange(event)

                  const nextValue = String(event.target.value ?? '')
                  const selectedOption = sortedOptions.find((opt) => opt.value === nextValue)

                  // Persist display label for summary
                  if (selectedOption && onFieldLabelResolved) {
                    const resolvedLabel = resolveOptionLabel(selectedOption)
                    onFieldLabelResolved({ [field.key]: resolvedLabel })
                  }

                  if (field.remoteOptions?.extraFields) {
                    for (const targetField of Object.keys(field.remoteOptions.extraFields)) {
                      setValue(targetField, selectedOption?.meta?.[targetField] as never, {
                        shouldDirty: true,
                        shouldValidate: false
                      })
                    }
                  }
                }}
                onBlur={f.onBlur}
                label={label}
                displayEmpty
                disabled={remoteLoading}
                endAdornment={remoteLoading ? <CircularProgress size={20} /> : undefined}
              >
                <MenuItem value="" disabled>
                  <em>{t('common.selectPlaceholder')}</em>
                </MenuItem>
                {sortedOptions.map((opt, index) => (
                  <MenuItem key={`${opt.value}-${index}`} value={opt.value}>
                    {resolveOptionLabel(opt)}
                  </MenuItem>
                ))}
              </Select>
              {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
              {field.helperText && !errorMessage && (
                <FormHelperText
                  sx={{
                    '& svg': {
                      verticalAlign: 'middle',
                      marginRight: 1
                    }
                  }}
                >
                  {hasHtmlHelperText ? (
                    <span dangerouslySetInnerHTML={{ __html: helperText ?? '' }} />
                  ) : (
                    helperText
                  )}
                </FormHelperText>
              )}
            </FormControl>
          )
        }}
      />
    )
  }

  // ── Textarea ──────────────────────────────────────────────
  if (field.type === 'textarea') {
    return (
      <Controller
        name={field.key}
        control={control}
        render={({ field: f }) => (
          <TextField
            {...f}
            label={label}
            placeholder={placeholder}
            required={field.required}
            error={!!errorMessage}
            helperText={errorMessage ?? helperText}
            multiline
            minRows={3}
            maxRows={6}
            variant="standard"
            fullWidth
            slotProps={{
              htmlInput: {
                maxLength: field.validation?.maxLength
              }
            }}
          />
        )}
      />
    )
  }

  // ── Date ──────────────────────────────────────────────────
  if (field.type === 'date') {
    return (
      <Controller
        name={field.key}
        control={control}
        render={({ field: f }) => (
          <TextField
            {...f}
            type="date"
            label={label}
            required={field.required}
            error={!!errorMessage}
            placeholder={placeholder}
            helperText={errorMessage ?? helperText}
            variant="standard"
            fullWidth
            slotProps={{
              inputLabel: { shrink: true }
            }}
          />
        )}
      />
    )
  }

  // ── Date Picker Airbnb ────────────────────────────────────
  if (field.type === 'date-picker-airbnb') {
    const minAge = field.validation?.minAge
    const isBirthDate = typeof minAge === 'number'

    const maxBirthDate =
      typeof minAge === 'number'
        ? (() => {
            const cutoff = new Date()
            cutoff.setHours(0, 0, 0, 0)
            cutoff.setFullYear(cutoff.getFullYear() - minAge)
            return cutoff
          })()
        : undefined

    return (
      <Controller
        name={field.key}
        control={control}
        render={({ field: f }) => {
          // Convert ISO strings to Date objects for DatePicker
          let dateValue: Date | null = null
          if (f.value instanceof Date) {
            dateValue = f.value
          } else if (typeof f.value === 'string' && f.value.trim()) {
            const parsed = new Date(f.value)
            if (!isNaN(parsed.getTime())) {
              dateValue = parsed
            }
          }

          return (
            <DatePickerAirbnb
              value={dateValue}
              onChange={f.onChange}
              onBlur={f.onBlur}
              label={label}
              placeholder={placeholder}
              error={!!errorMessage}
              helperText={errorMessage ?? helperText}
              disabled={remoteLoading}
              disablePast={!isBirthDate}
              maxDate={maxBirthDate}
              disabledMonths={field.validation?.disabledMonths}
            />
          )
        }}
      />
    )
  }

  // ── Default: text, email, tel, number ─────────────────────
  const inputType =
    field.type === 'number'
      ? 'number'
      : field.type === 'email'
        ? 'email'
        : field.type === 'tel'
          ? 'tel'
          : 'text'

  return (
    <Controller
      name={field.key}
      control={control}
      render={({ field: f }) => (
        <TextField
          {...f}
          type={inputType}
          label={label}
          placeholder={placeholder}
          required={field.required}
          error={!!errorMessage}
          helperText={errorMessage ?? helperText}
          variant="standard"
          fullWidth
          onBlur={(event) => {
            f.onBlur()

            if (field.type !== 'email') return

            const email = String(event.target.value ?? '')
              .trim()
              .toLowerCase()
            const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

            if (!isEmailValid || email === lastVerifiedEmailRef.current) return

            if (verifyDebounceTimerRef.current !== null) {
              window.clearTimeout(verifyDebounceTimerRef.current)
            }

            verifyDebounceTimerRef.current = window.setTimeout(() => {
              lastVerifiedEmailRef.current = email
              void (async () => {
                try {
                  const data = await verifyOldDraft(email)

                  if (data.returnCode === 'OK' && data.body?.formPresent) {
                    onEmailDraftFound?.(email)
                  }
                } catch {
                  // Silent fail: verifyold should not block field interaction
                }
              })()
            }, 450)
          }}
          slotProps={{
            inputLabel: { shrink: placeholder ? true : undefined }
          }}
        />
      )}
    />
  )
}
